import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import InfoHint from '../common/InfoHint';

/**
 * Busy-day traffic waves ("banks"). This is a STATISTICAL wave detector run on the
 * single IATA busy day: it flags every 60-min window (stepped every 30 min) that
 * exceeds a movement threshold (DACAMS default: >=30 total, >=20 arr, >=20 dep)
 * and groups contiguous windows. It is NOT the airport's scheduled connection
 * banks — so its count and durations will not match the operator's nominal hub
 * waves. Moved out of the As-Is Overview (it is analysis, not an As-Is fact) and
 * reframed here so the distinction is explicit.
 */
export interface BankAnalysisProps {
  bankData: any;
  bankChartData: any[];
  language: 'es' | 'en';
}

const BankAnalysis: React.FC<BankAnalysisProps> = ({ bankData, bankChartData, language }) => {
  const es = language === 'es';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  const axis = 'var(--text-tertiary)';
  const grid = 'var(--border-subtle)';

  if (!(bankChartData.length > 0 && (bankData.arr_banks?.length > 0 || bankData.dep_banks?.length > 0))) {
    return null;
  }

  return (
    <div className="pro-card p-6">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className={`font-bold text-sm ${textMain} flex items-center gap-1.5`}>
            {es ? 'Olas de tráfico del busy day' : 'Busy-day traffic waves'}
            <InfoHint
              align="left"
              text={es
                ? 'Detector estadístico de "olas" (bancos) sobre el IATA Busy Day: marca cada ventana de 60 min (cada 30 min) que supera un umbral de movimientos (por defecto DACAMS: ≥30 total, ≥20 llegadas, ≥20 salidas) y agrupa las contiguas. OJO: NO son los bancos de conexiones PROGRAMADOS del hub, así que su número y duración no tienen por qué coincidir con las olas nominales del aeropuerto. Es una lectura de la forma de la demanda de ese día concreto.'
                : 'Statistical "wave" (bank) detector on the IATA Busy Day: it flags every 60-min window (stepped every 30 min) that exceeds a movement threshold (DACAMS default: ≥30 total, ≥20 arrivals, ≥20 departures) and groups contiguous ones. NOTE: these are NOT the hub\'s SCHEDULED connection banks, so their count and duration need not match the airport\'s nominal waves. It is a read of that specific day\'s demand shape.'}
            />
          </h4>
          {bankData.busy_day && (
            <span className={`text-xs ${textSub}`}>{bankData.busy_day} · {bankData.total_arr_banks || 0} {es ? 'olas ARR' : 'ARR waves'}, {bankData.total_dep_banks || 0} {es ? 'olas DEP' : 'DEP waves'}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--aipron-accent)' }}></div>
            <span className={textMain}>{es ? 'Llegadas' : 'Arrivals'}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-bold uppercase">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--node-rrc)' }}></div>
            <span className={textMain}>{es ? 'Salidas' : 'Departures'}</span>
          </div>
        </div>
      </div>
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={bankChartData}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
            <XAxis dataKey="time" stroke={axis} fontSize={10} tickLine={false} axisLine={false} interval={3} angle={-45} textAnchor="end" height={50} />
            <YAxis stroke={axis} fontSize={12} tickLine={false} axisLine={false} label={{ value: es ? 'vuelos / 15 min' : 'flights / 15 min', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: axis } }} />
            <Tooltip
              contentStyle={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border)', borderRadius: '8px', color: 'var(--text-primary)' }}
              itemStyle={{ color: 'var(--text-primary)' }}
              formatter={(value: number, name: string) => {
                const label = name === 'arrivals' ? (es ? 'Llegadas' : 'Arrivals') : (es ? 'Salidas' : 'Departures');
                return [value, label];
              }}
            />
            <Bar dataKey="arrivals" fill="var(--aipron-accent)" fillOpacity={0.85} radius={[2, 2, 0, 0]} />
            <Bar dataKey="departures" fill="var(--node-rrc)" fillOpacity={0.85} radius={[2, 2, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Wave tables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        {bankData.arr_banks?.length > 0 && (
          <BankTable title={es ? 'Olas de Llegadas' : 'Arrival Waves'} rows={bankData.arr_banks} token="var(--aipron-accent)" language={es ? 'es' : 'en'} />
        )}
        {bankData.dep_banks?.length > 0 && (
          <BankTable title={es ? 'Olas de Salidas' : 'Departure Waves'} rows={bankData.dep_banks} token="var(--node-rrc)" language={es ? 'es' : 'en'} />
        )}
        {bankData.total_banks?.length > 0 && (
          <BankTable title={es ? 'Olas Totales' : 'Total Waves'} rows={bankData.total_banks} token="var(--aipron-success)" language={es ? 'es' : 'en'} totalCol />
        )}
      </div>
    </div>
  );
};

/** One wave table (ARR / DEP / TOTAL), styled with the data-table tokens. */
const BankTable: React.FC<{ title: string; rows: any[]; token: string; language: 'es' | 'en'; totalCol?: boolean }> = ({ title, rows, token, language, totalCol }) => {
  const es = language === 'es';
  return (
    <div className="rounded-lg border border-[var(--border-subtle)] p-3" style={{ background: `color-mix(in srgb, ${token} 6%, transparent)` }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: token }}></div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: token }}>{title}</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
            <th className="text-left py-1 pr-1 font-bold">ID</th>
            <th className="text-left py-1 pr-1 font-bold">UTC</th>
            <th className="text-right py-1 pr-1 font-bold">{totalCol ? (es ? 'Mov.' : 'Mov.') : (es ? 'Vuelos' : 'Flights')}</th>
            <th className="text-right py-1 pr-1 font-bold">{es ? 'Pico/h' : 'Peak/h'}</th>
            <th className="text-right py-1 font-bold">{es ? 'Dur.' : 'Dur.'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b: any) => (
            <tr key={b.bank_id} className="border-b border-[var(--border-subtle)] text-[var(--text-secondary)]">
              <td className="py-0.5 pr-1 font-mono font-bold" style={{ color: token }}>{b.bank_id}</td>
              <td className="py-0.5 pr-1 font-mono tabular-nums">{b.start_utc}–{b.end_utc}</td>
              <td className="py-0.5 pr-1 text-right font-mono tabular-nums">{b.flights}</td>
              <td className="py-0.5 pr-1 text-right font-bold font-mono tabular-nums">{b.peak_rate_hr}</td>
              <td className="py-0.5 text-right font-mono tabular-nums">{b.duration_min}m</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BankAnalysis;
